import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { isEventbriteEnabled } from "../eventbrite/eventbriteFeature";
import { InitialAttendeeImportContext } from "./InitialAttendeeImportContext";
import { toast } from "react-toastify";
import { useAuth } from "../auth/AuthContext";
import BadgeEditor from "../components/BadgeEditor";
import SuccessToast, { useSuccessToast } from "../components/SuccessToast";
import { getBadgeSizeTrackingValue, trackEvent } from "../helpers/trackEvent";
import { createEvent, updateEvent, validateNewEventName } from "./eventsService";
import "./Events.css";

const NewEventPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [initialImport] = useState(() => isEventbriteEnabled() ? location.state?.eventbriteImport ?? null : null);
  const [createdEvent, setCreatedEvent] = useState(null);
  const { showSuccessToast, successToast } = useSuccessToast();
  const saveTimerRef = useRef(null);
  const pendingSavePayloadRef = useRef(null);
  const createdEventRef = useRef(null);
  const isUnmountingRef = useRef(false);
  const saveRequestIdRef = useRef(0);

  useEffect(() => {
    createdEventRef.current = createdEvent;
  }, [createdEvent]);

  useEffect(() => () => {
    isUnmountingRef.current = true;
    clearTimeout(saveTimerRef.current);
    const pendingPayload = pendingSavePayloadRef.current;
    const eventToSave = createdEventRef.current;
    pendingSavePayloadRef.current = null;

    if (pendingPayload && eventToSave) {
      updateEvent(eventToSave.id, user.id, {
        name: eventToSave.name,
        ...pendingPayload,
      }).catch((saveError) => {
        console.error("Could not finish saving event in background.", saveError);
      });
    }
  }, [user.id]);

  const handleEventCreate = async ({ badgeSize, badgeTemplate, importedSpreadsheetData }) => {
    try {
      const event = await createEvent({
        userId: user.id,
        name: importedSpreadsheetData.eventName,
        badgeSize,
        badgeTemplate,
        importedSpreadsheetData,
      });
      trackEvent({
        action: "event_created",
        data: {
          attendeesCount: importedSpreadsheetData?.fileData?.data?.length ?? 0,
          badgeSize: getBadgeSizeTrackingValue(badgeSize),
        },
      });
      window.history.replaceState(
        window.history.state,
        "",
        `/events/${event.id}`
      );
      setCreatedEvent(event);
      showSuccessToast("Event created");
    } catch (error) {
      toast.error(error.message || "Could not create event.");
      throw error;
    }
  };

  const handleEventChange = useMemo(() => {
    if (!createdEvent) {
      return undefined;
    }

    return (payload) => {
      if (isUnmountingRef.current) {
        const eventToSave = createdEventRef.current;
        if (eventToSave) {
          updateEvent(eventToSave.id, user.id, {
            name: eventToSave.name,
            ...payload,
          }).catch((saveError) => {
            console.error("Could not finish saving event in background.", saveError);
          });
        }
        return;
      }

      pendingSavePayloadRef.current = payload;
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        const payloadToSave = pendingSavePayloadRef.current;
        const eventToSave = createdEventRef.current;
        pendingSavePayloadRef.current = null;
        saveTimerRef.current = null;

        if (!payloadToSave || !eventToSave) {
          return;
        }

        const saveRequestId = saveRequestIdRef.current + 1;
        saveRequestIdRef.current = saveRequestId;

        try {
          const updatedEvent = await updateEvent(eventToSave.id, user.id, {
            name: eventToSave.name,
            ...payloadToSave,
          });
          if (saveRequestIdRef.current === saveRequestId && !isUnmountingRef.current) {
            setCreatedEvent(updatedEvent);
          }
        } catch (saveError) {
          if (saveRequestIdRef.current === saveRequestId && !isUnmountingRef.current) {
            toast.error(saveError.message || "Could not save event changes.");
          }
        }
      }, 900);
    };
  }, [createdEvent, user.id]);

  return (
    <InitialAttendeeImportContext.Provider value={initialImport}>
      <BadgeEditor
        mode={createdEvent ? "event" : "create-event"}
        eventData={createdEvent}
        onEventChange={handleEventChange}
        onEventCreate={createdEvent ? undefined : handleEventCreate}
        onEventNameValidate={(name) =>
          validateNewEventName({ name, userId: user.id })
        }
        headerProps={{
          backTo: "/events",
          backLabel: "Back to events",
          title: createdEvent?.name,
        }}
      />
      {successToast && (
        <SuccessToast key={successToast.id} title={successToast.title} />
      )}
    </InitialAttendeeImportContext.Provider>
  );
};

export default NewEventPage;

